import { Component, Input, Output, EventEmitter, OnInit, ElementRef, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

/**
 * NOTE:
 * - Configura environment.apiUrl con la base del tuo RestApiUrl (come Properties.Settings.Default.RestApiUrl).
 * - Per tipi complessi (masking, combo avanzate, multi-select) puoi sostituire con librerie (Angular Material, ngx-mask, etc).
 */

export interface DrkForm {
    Id: number;
    FormName: string;
    FormValidators?: string; // JSON string like in original
}

export interface DrkFormField {
    Id: number;
    Name: string;
    ControlType: string; // TEXTBOX, TEXTAREA, INTTEXTBOX, DECTEXTBOX, DROPDOWNLIST, LISTBOX, DATEPICKER, CHECKBOX, FILE, LABEL, BUTTON, RECTANGLE, IMAGE
    X: number;
    Y: number;
    Width: number;
    Height: number;
    Tooltip?: string;
    Required?: boolean;
    BackgroundColor?: string;
    Color?: string;
    BorderColor?: string;
    DefaultValue?: string;
    Action?: string; // for buttons: SUBMIT / CANCEL / SECONDARY / FORMACTION
    Options?: string; // JSON string or other (for dropdown/listbox)
    // ... any other fields you need
}

@Component({
    selector: 'app-drake-free-form',
    templateUrl: './drake-free-form.component.html',
    styleUrls: ['./drake-free-form.component.scss']
})
export class DrakeFreeFormComponent implements OnInit {
ìì
    @Input() client: any;
    @Input() callingTopic?: string;
    @Input() paramData?: { [k: string]: any } = {};
    @Input() language?: string;
    @Input() connected: boolean = false;

    @Output() submitted = new EventEmitter<{ valuesJson: string }>();
    @Output() cancelled = new EventEmitter<void>();
    @Output() secondary = new EventEmitter<any>();

    public fields: DrkFormField[] = [];
    public formGroup: FormGroup = new FormGroup({});
    public invalidFields: string[] = [];
    public closedAs: string = '';
    public apiBase = (window as any).__env?.apiUrl || '/'; // set your environment api URL

    // container dimensions (approx from the original)
    public wrapperWidth = 1200;
    public wrapperHeight = 800;

    constructor(private http: HttpClient, private el: ElementRef,
        @Inject(MAT_DIALOG_DATA) public data: any,) { }

    ngOnInit(): void {
        if (!this.data.form) {
            console.error('Form non fornito.');
            return;
        }
        this.initializeForm(this.data.form);
    }

    private initializeForm(form: DrkForm) {
        // imposta titolo (puoi esporre come property o usarlo in template)
        // scarica i campi (equivalente restClient.Execute<List<ImFormField>>)
        const url = `${this.apiBase}iemmedb/getformfields/${form.Id}`;
        this.http.get<DrkFormField[]>(url).subscribe(fields => {
            this.fields = fields || [];
            // calcola dimensioni wrapper (simile al tuo codice WPF)
            const firstBottom = this.fields.reduce((p, c) => (c.Y + c.Height > (p?.Y ?? -1 + (p?.Height ?? 0))) ? c : p, this.fields[0] || null);
            const firstRight = this.fields.reduce((p, c) => (c.X + c.Width > (p?.X ?? -1 + (p?.Width ?? 0))) ? c : p, this.fields[0] || null);

            if (firstRight) this.wrapperWidth = Math.max(firstRight.X + firstRight.Width + 20, this.wrapperWidth);
            if (firstBottom) this.wrapperHeight = Math.max(firstBottom.Y + firstBottom.Height + 20 + 40, this.wrapperHeight);

            // crea form controls dinamicamente
            this.fields.forEach(f => {
                const defaultVal = this.resolveDefaultValue(f);
                const validators = [];
                if (f.Required) validators.push(Validators.required);
                // per INTTEXTBOX / DECTEXTBOX potresti aggiungere pattern validation
                this.formGroup.addControl(f.Name, new FormControl(defaultVal, validators));
            });
        }, err => {
            console.error('Errore recupero campi form', err);
        });
    }

    private resolveDefaultValue(f: DrkFormField) {
        // usa DefaultValue o ParamData come in WPF
        if (f.DefaultValue) return f.DefaultValue;
        if (this.paramData && this.paramData[f.Name] !== undefined) return this.paramData[f.Name];
        return '';
    }

    public cancelForm() {
        this.closedAs = 'CANCEL';
        this.cancelled.emit();
    }

    public secondaryForm(field?: DrkFormField) {
        this.closedAs = 'SECONDARY';
        let result = null;
        // se il bottone ha Tag (equivalente), assumiamo f.DefaultValue o Options
        if (field && field.DefaultValue) result = field.DefaultValue;
        this.secondary.emit(result);
    }

    public actionForm(field?: DrkFormField) {
        // TODO: implementare azioni form-specifiche
        console.log('ActionForm called for', field);
    }

    public submitForm(buttonField?: DrkFormField) {
        this.closedAs = 'SUBMIT';
        this.invalidFields = [];

        // check required fields
        this.fields.forEach(f => {
            if (f.Required) {
                const ctrl = this.formGroup.controls[f.Name];
                const val = ctrl?.value;
                if (val === null || val === undefined || (String(val).trim?.() === '')) {
                    this.invalidFields.push(f.Name);
                } else {
                    // per numeric 0 potrebbe essere considerato invalido nel codice originale: se si vuole replicare:
                    if ((f.ControlType === 'INTTEXTBOX' || f.ControlType === 'DECTEXTBOX') && Number(val) === 0) {
                        this.invalidFields.push(f.Name);
                    }
                }
            }
        });

        if (this.invalidFields.length > 0) {
            alert('Compilare tutti i campi obbligatori:\n-' + this.invalidFields.join('\n- '));
            return;
        }

        // validators custom come in _form.FormValidators (json string)
        if (this.data.form.FormValidators) {
            try {
                const validators = JSON.parse(this.data.form.FormValidators) as Array<{ Val1: string, Val2: string, Op: string, Message: string }>;
                for (const val of validators) {
                    const val1 = this.parseField(val.Val1);
                    const val2 = this.parseField(val.Val2);
                    let ok = false;
                    switch (val.Op) {
                        case '==': ok = (val1 === val2); break;
                        case '!=': ok = (val1 !== val2); break;
                        case '>': ok = (Number(val1) > Number(val2)); break;
                        case '>=': ok = (Number(val1) >= Number(val2)); break;
                        case '<': ok = (Number(val1) < Number(val2)); break;
                        case '<=': ok = (Number(val1) <= Number(val2)); break;
                        default: ok = true; break;
                    }
                    if (!ok) {
                        alert(val.Message);
                        return;
                    }
                }
            } catch (e) {
                console.warn('FormValidators non in formato JSON/leggibile', e);
            }
        }

        // costruisci valori come dictionary
        const values: { [k: string]: any } = {};
        Object.keys(this.formGroup.controls).forEach(name => {
            values[name] = this.formGroup.controls[name].value;
        });

        const sendingValues = JSON.stringify(values);
        // emetti evento con JSON (equivalente di Result = sendingValues; Close();)
        this.submitted.emit({ valuesJson: sendingValues });
    }

    private parseField(valExpr: string): string {
        // replica ParseField: trova pattern {F:FieldName}
        if (!valExpr) return '';
        let result = valExpr;
        const regex = /\{[F]:[\w\.]+\}/g;
        let matches = result.match(regex);
        while (matches && matches.length > 0) {
            for (const m of matches) {
                const fieldName = m.replace('{F:', '').replace('}', '');
                const ctrl = this.formGroup.controls[fieldName];
                let matchValue = '';
                if (ctrl) {
                    const v = ctrl.value;
                    matchValue = (v !== null && v !== undefined) ? String(v) : '';
                } else {
                    // se controllo non trovato -> empty
                    matchValue = '';
                }
                result = result.replace(m, matchValue);
            }
            matches = result.match(regex);
        }
        return result;
    }

    // helper per options (JSON string -> array)
    public parseOptions(opt?: string): Array<{ key: string, value: string }> {
        if (!opt) return [];
        // supporta due formati: JSON object {"k":"v"} o "key1;key2" (listbox default)
        try {
            const obj = JSON.parse(opt);
            if (typeof obj === 'object') {
                return Object.keys(obj).map(k => ({ key: k, value: obj[k] }));
            }
        } catch { /* not json */ }

        // fallback list of keys separated by ;
        return opt.split(';').map(k => ({ key: k, value: k }));
    }

    // utilita per style inline (left/top/width/height)
    public ctrlStyle(f: DrkFormField) {
        return {
            left: `${f.X}px`,
            top: `${f.Y}px`,
            width: `${f.Width}px`,
            height: `${f.Height}px`,
            position: 'absolute'
        } as any;
    }

    // gestione file input
    public onFileChange(event: any, fieldName: string) {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            // manteniamo come base64 (o eventualmente inviamo bytes)
            this.formGroup.controls[fieldName].setValue(reader.result?.toString() || '');
        };
        reader.readAsDataURL(file);
    }

    onMultiSelectChange(event: any, controlName: string) {
        const values = Array.from(event.target.selectedOptions).map((x: any) => x.value);
        this.formGroup.controls[controlName].setValue(values);
    }
}